import { Component, Event, EventEmitter, h, Method, Prop, State, Watch } from "@stencil/core"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { getCountryOptions } from "../../countries/CountryOptions"
import { CountryOption } from "../../countries/CountryOption.model"
import { reconcileControlledValue } from "../../helpers/reconcileControlledValue"
import { getSupportedLocaleData, getLanguageHandler } from "../../languageHandler/LanguageHandler"
import inputErrorIcon from "../../styles/inputErrorIcon.svg"

let selectCountryId = 0

/** Controlled ISO country select. */
@Component({
  tag: "select-country",
  styleUrl: "SelectCountry.scss",
  shadow: true,
})
export class SelectCountry {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /** Current controlled ISO 3166-1 alpha-2 value. */
  @Prop() value: string = ""

  /** Optional canonical country-code allowlist. */
  @Prop() allowedCountries?: string[] = []

  /** Whether the select is required. */
  @Prop() required?: boolean

  /** Whether the select is disabled. */
  @Prop() disabled?: boolean

  /** Whether the select should be rendered in its invalid state. */
  @Prop() invalid?: boolean

  /** Translation key used for an externally controlled validation message. */
  @Prop() errorMessageKey?: string

  /** Fully translated validation message. Takes precedence over errorMessageKey. */
  @Prop() errorMessage?: string

  /** Translation key used for the empty option. Defaults to the visible label. */
  @Prop() placeholderKey: string = "countryRegion"

  /** Emitted whenever the user selects a country code. */
  @Event() valueChange: EventEmitter<string>

  /** Emitted when focus leaves the select. */
  @Event() fieldBlur: EventEmitter<void>

  @State() countries: CountryOption[] = []

  private languageHandler: LanguageHandler
  private displayLanguage: string
  private readonly selectId = `select-country-${selectCountryId++}`
  private selectElement?: HTMLSelectElement

  componentWillLoad() {
    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.displayLanguage = localeData.lang
    this.updateCountries()
  }

  @Watch("allowedCountries")
  updateCountries() {
    this.countries = getCountryOptions(this.displayLanguage, this.allowedCountries)
  }

  private getDisplayValue(): string {
    return this.value || ""
  }

  private onChange = (event: Event) => {
    const select = event.target as HTMLSelectElement
    const selectedCountry = this.countries.find(country => country.code === select.value)

    if (!selectedCountry) {
      return
    }

    const countryCode = selectedCountry.code

    this.valueChange.emit(countryCode)
    reconcileControlledValue(select, () => this.getDisplayValue())
  }

  private onBlur = () => {
    this.fieldBlur.emit()
  }

  /** Focuses the native country select. */
  @Method()
  async focusInput() {
    this.selectElement?.focus()
  }

  render() {
    const currentValue = this.getDisplayValue()
    const hasCurrentOption = this.countries.some(country => country.code === currentValue)
    const unavailableCurrentCountry =
      currentValue && !hasCurrentOption
        ? getCountryOptions(this.displayLanguage).find(country => country.code === currentValue)
        : undefined
    const errorId = `${this.selectId}-error`

    return (
      <div class="selectCountry">
        <label class="inputLabel" htmlFor={this.selectId}>
          {this.languageHandler.getString("countryRegion")}
        </label>
        <div class="inputContainer">
          <select
            ref={element => (this.selectElement = element)}
            id={this.selectId}
            class="input"
            onChange={this.onChange}
            onBlur={this.onBlur}
            required={this.required}
            disabled={this.disabled}
            aria-invalid={this.invalid ? "true" : "false"}
            aria-describedby={this.invalid ? errorId : undefined}>
            <option value="" disabled hidden selected={!currentValue}>
              {this.languageHandler.getString(this.placeholderKey)}
            </option>
            {currentValue && !hasCurrentOption && (
              <option value={currentValue} selected hidden>
                {unavailableCurrentCountry?.name || currentValue}
              </option>
            )}
            {this.countries.map(country => (
              <option value={country.code} key={country.code} selected={country.code === currentValue}>
                {country.name}
              </option>
            ))}
          </select>
          {this.invalid && <img class="errorIcon" src={inputErrorIcon} alt="" aria-hidden="true" />}
        </div>
        {this.invalid && (
          <div id={errorId} class="errorInfo" role="alert">
            {this.errorMessage || this.languageHandler.getString(this.errorMessageKey || "requiredField")}
          </div>
        )}
      </div>
    )
  }
}
